<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import pandora from '@/assets/logos/logo-pandora.svg'
import oidcAuth from '@/auth/oidcAuth'
import { useAuthStore } from '@/stores/useAuthStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { clearLocalStoragePreservingSchema } from '@/auth/versionGate'

const router = useRouter()

onMounted(async () => {
  const boostCoreStore = useBoostCorePermissionStore()
  const wasPasswordAuth = boostCoreStore.authMethod === 'password'

  if (!wasPasswordAuth) {
    // Properly stop the Azure silent-renew timer AND clear the OIDC user.
    // Without this, oidc-client-ts keeps an internal timer that triggers a
    // hidden signinSilent iframe (→ /auth/signinsilent/main) even after we've
    // signed out from boostcore. removeUser() also fires addUserUnloaded, so
    // any other tab listening to that event reacts as well.
    try { await oidcAuth.mgr.removeUser() } catch { /* ignore */ }
    // Defensive: scrub anything else still in localStorage. Preserves the
    // versionGate stamp so the next top-level navigation (e.g. Azure → OIDC
    // callback) doesn't re-trigger the gate (extra logout, sessionStorage wipe).
    clearLocalStoragePreservingSchema()
  }

  const authStore = useAuthStore()
  await authStore.coreLogout()
  boostCoreStore.clear()

  // SPA navigate — no full bundle reload.
  router.push('/auth/login')
})
</script>

<template>
  <div class="flex flex-col w-full h-full justify-center">
    <div class="flex flex-col w-full justify-center items-center gap-2 mb-2">
      <p>Déconnexion en cours...</p>
    </div>
    <div class="flex justify-center items-center gap-1 mt-4">
      <div class="floating-wrapper">
        <component :is="pandora" class="w-40 floating" />
        <div class="floating-shadow shadow4"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.floating {
  animation: floating 3s ease-in-out infinite;
}

@keyframes floating {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0);
  }
}
.floating-wrapper {
  position: relative;
  display: inline-block;
}

.floating-shadow {
  position: absolute;
  left: 50%;
  bottom: -10px;
  width: 80%;
  height: 10px;
  border-radius: 50%;
  transform: translateX(-100%) scaleX(1);
  background: radial-gradient(ellipse at center, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 70%);
  filter: blur(2px);
  pointer-events: none;
  animation: floatingShadow 3s ease-in-out infinite;
}

@keyframes floatingShadow {
  0% {
    transform: translateX(-50%) scaleX(1);
    opacity: 0.5;
  }
  50% {
    transform: translateX(-50%) scaleX(0.85);
    opacity: 0.18;
  }
  100% {
    transform: translateX(-50%) scaleX(1);
    opacity: 0.5;
  }
}
</style>
